<template>
	<div class="relative w-full" :class="[error ? 'error' : '']">
		<Multiselect
			ref="multiselect"
			v-model="selected"
			class="form-multiselect"
			:class="[
				error
					? 'rounded border border-red-400 border-opacity-80'
					: 'border-gray-200 focus:border-blue-400',
			]"
			:classes="{
				container: 'multiselect p-1',
				options: `multiselect-options list-none m-0 ${maxHeight}`,
				groupOptions: 'multiselect-group-options list-none m-0',
				groupLabel: 'multiselect-group-label bg-transparent p-0',
				singleLabel: [
					showLabel
						? 'multiselect-single-label px-2 pt-3'
						: 'multiselect-single-label px-2',
				],
				containerActive: 'shadow-none',
			}"
			:options="options"
			:is-disabled="isDisabled"
			:mode="mode"
			:can-clear="isClearable"
			:can-deselect="false"
			:searchable="isSearchable"
			:groups="false"
			:open-direction="directionToOpen"
			:object="object"
			:loading="loading"
			@select="onSelect(selected)"
			@open="onOpen"
			@search-change="onSearchChange"
		/>
		<label
			class="pointer-events-none absolute top-6 origin-top-left -translate-y-1/2 px-3 text-base duration-300"
			:class="
				(selected && showLabel) || isSearching
					? `-translate-y-[15px] scale-75 transform px-4 text-blue-400 ${additionalLabelClass}`
					: `${additionalLabelClass}`
			"
		>
			{{ labelName }}
		</label>
		<div
			v-if="error && showError"
			class="mt-1.5 flex flex-row justify-start"
		>
			<div class="text-sm leading-none text-red-400 opacity-70">
				{{ error }}
			</div>
		</div>

		<Transition name="fade">
			<div v-if="isNextStep" class="border-highlight">
				<div class="mask"></div>
			</div>
		</Transition>
	</div>
</template>

<script setup>
import Multiselect from '@vueform/multiselect'

const props = defineProps({
	modelValue: {
		type: [String, Number, Array, Object],
	},
	error: {
		type: [String, Boolean],
		default: false,
		required: false,
	},
	labelName: {
		type: String,
		default: 'Label Name',
		required: true,
	},
	options: {
		type: [Array, Object],
		default: () => ({ value: '', label: '' }),
		required: true,
	},
	isDisabled: {
		type: Boolean,
		default: false,
		required: false,
	},
	showError: {
		type: Boolean,
		default: true,
		required: false,
	},
	isClearable: {
		type: Boolean,
		default: false,
		required: false,
	},
	multiple: {
		type: Boolean,
		default: false,
		required: false,
	},
	maxHeight: {
		type: String,
		default: 'max-h-60',
		required: false,
	},
	additionalLabelClass: {
		type: String,
		default: '',
		required: false,
	},
	openTopDirection: {
		type: Boolean,
		default: false,
		required: false,
	},
	showLabel: {
		type: Boolean,
		default: true,
		required: false,
	},
	loading: {
		type: Boolean,
		default: false,
		required: false,
	},
	isSearchable: {
		type: Boolean,
		default: false,
		required: false,
	},
	isNextStep: {
		type: Boolean,
		default: false,
		required: false,
	},
})

const multiselect = ref()

const selected = ref()

const mode = computed(() => (props.multiple ? 'multiple' : 'single'))

const object = ref(false)

const directionToOpen = computed(() =>
	props.openTopDirection ? 'top' : 'bottom'
)

const emit = defineEmits(['update:modelValue', 'getValueManualSelect', 'open'])

const onSelect = (value) => {
	emit('update:modelValue', value)
	emit('getValueManualSelect', value)
}

const onOpen = () => {
	emit('open')
}

const isSearching = ref(false)

const onSearchChange = () => {
	// Reset the label if search input is empty.
	if (multiselect.value.search === '') {
		isSearching.value = false
	} else {
		isSearching.value = true
	}
}

const reset = () => {
	multiselect.value.clear()
}

const select = (id) => {
	multiselect.value.select(id)
}

const preselectFirstOption = () => {
	object.value = true
	const [preselectedOption] = props.options
	selected.value = preselectedOption
}

defineExpose({ reset, preselectFirstOption, select })
</script>
